/**
 * Hook for video file upload with client-side validation
 * Phase 5B: Client-Side Video Validation
 *
 * Extracts metadata from video files using HTML5 video element and validates
 * against quality requirements before upload. This provides immediate feedback
 * to users without waiting for backend processing.
 */

import { useCallback, useState } from "react";

/**
 * Video metadata extracted from HTML5 video element
 */
export interface VideoClientMetadata {
  duration: number; // seconds
  width: number;
  height: number;
  aspectRatio: string;
}

/**
 * Validation result with blocking errors and non-blocking warnings
 */
export interface VideoValidationResult {
  valid: boolean;
  errors: string[]; // Blocking - upload not allowed
  warnings: string[]; // Non-blocking - upload allowed with warnings
}

/**
 * Combined metadata and validation state
 */
export interface VideoMetadataState {
  metadata: VideoClientMetadata | null;
  validation: VideoValidationResult | null;
  extractionFailed: boolean; // True if HTML5 extraction failed (upload still allowed)
}

/**
 * Validation options based on asset kind
 */
interface VideoValidationOptions {
  assetKind: "FEATURE" | "TRAILER";
  fileSize: number;
  maxSizeGB: number;
}

// === Validation Constants ===

const MIN_RESOLUTION = { width: 1920, height: 1080 };
const RECOMMENDED_RESOLUTION = { width: 3840, height: 2160 };

const DURATION_CONSTRAINTS: Record<string, { min: number; max: number; recommendedMax?: number }> = {
  FEATURE: {
    min: 40 * 60, // 40 minutes
    max: 6 * 60 * 60, // 6 hours
    recommendedMax: 4 * 60 * 60, // 4 hours
  },
  TRAILER: {
    min: 15, // 15 seconds
    max: 5 * 60, // 5 minutes
    recommendedMax: 3 * 60, // 3 minutes
  },
};

const EXPECTED_ASPECT_RATIOS = ["16:9", "21:9", "2.39:1", "1.85:1", "4:3"];

// === Helper Functions ===

/**
 * Format duration in human-readable format
 */
function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

/**
 * Format file size in human-readable format
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Calculate aspect ratio string from width and height
 */
function calculateAspectRatio(width: number, height: number): string {
  const ratio = width / height;
  if (Math.abs(ratio - 16 / 9) < 0.02) return "16:9";
  if (Math.abs(ratio - 21 / 9) < 0.02) return "21:9";
  if (Math.abs(ratio - 2.39) < 0.02) return "2.39:1";
  if (Math.abs(ratio - 1.85) < 0.02) return "1.85:1";
  if (Math.abs(ratio - 4 / 3) < 0.02) return "4:3";

  // Return simplified ratio for non-standard
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(Math.round(width), Math.round(height));
  return `${Math.round(width / divisor)}:${Math.round(height / divisor)}`;
}

/**
 * Check if aspect ratio matches expected values
 */
function isExpectedAspectRatio(aspectRatio: string): boolean {
  const parseRatio = (ratio: string): number => {
    if (ratio.includes(":")) {
      const [w, h] = ratio.split(":").map(Number);
      return w / h;
    }
    return Number.parseFloat(ratio);
  };

  const actualRatio = parseRatio(aspectRatio);
  return EXPECTED_ASPECT_RATIOS.some((expected) => {
    const expectedRatio = parseRatio(expected);
    return Math.abs(actualRatio - expectedRatio) / expectedRatio <= 0.05;
  });
}

/**
 * Extract video metadata using HTML5 video element
 * This is a fast, client-side operation that only reads file headers
 */
export async function extractVideoMetadata(file: File): Promise<VideoClientMetadata> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";

    const timeoutId = setTimeout(() => {
      URL.revokeObjectURL(video.src);
      reject(new Error("Metadata extraction timed out"));
    }, 10000); // 10 second timeout

    video.onloadedmetadata = () => {
      clearTimeout(timeoutId);
      const metadata: VideoClientMetadata = {
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
        aspectRatio: calculateAspectRatio(video.videoWidth, video.videoHeight),
      };
      URL.revokeObjectURL(video.src);
      resolve(metadata);
    };

    video.onerror = () => {
      clearTimeout(timeoutId);
      URL.revokeObjectURL(video.src);
      reject(new Error("Failed to load video metadata"));
    };

    video.src = URL.createObjectURL(file);
  });
}

/**
 * Validate video metadata against quality requirements
 */
export function validateVideoMetadata(
  metadata: VideoClientMetadata,
  options: VideoValidationOptions,
): VideoValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const { assetKind, fileSize, maxSizeGB } = options;

  // === BLOCKING ERRORS ===

  // Resolution validation (blocking)
  if (metadata.width < MIN_RESOLUTION.width || metadata.height < MIN_RESOLUTION.height) {
    errors.push(
      `Resolution ${metadata.width}×${metadata.height} is below minimum ` +
        `${MIN_RESOLUTION.width}×${MIN_RESOLUTION.height} for streaming quality`,
    );
  }

  // Duration validation (blocking)
  const constraints = DURATION_CONSTRAINTS[assetKind];
  if (constraints) {
    if (metadata.duration < constraints.min) {
      errors.push(
        `Duration ${formatDuration(metadata.duration)} is below minimum ` +
          `${formatDuration(constraints.min)} for ${assetKind.toLowerCase()}`,
      );
    }
    if (metadata.duration > constraints.max) {
      errors.push(
        `Duration ${formatDuration(metadata.duration)} exceeds maximum ` +
          `${formatDuration(constraints.max)} for ${assetKind.toLowerCase()}`,
      );
    }
  }

  // File size validation (blocking for configured max)
  const maxBytes = maxSizeGB * 1024 * 1024 * 1024;
  if (fileSize > maxBytes) {
    errors.push(`File size ${formatFileSize(fileSize)} exceeds maximum ${maxSizeGB}GB`);
  }

  // === NON-BLOCKING WARNINGS ===

  // Resolution warning (below recommended but above minimum)
  if (
    metadata.width >= MIN_RESOLUTION.width &&
    metadata.height >= MIN_RESOLUTION.height &&
    (metadata.width < RECOMMENDED_RESOLUTION.width || metadata.height < RECOMMENDED_RESOLUTION.height)
  ) {
    warnings.push(
      `Resolution ${metadata.width}×${metadata.height} is below recommended ` +
        `${RECOMMENDED_RESOLUTION.width}×${RECOMMENDED_RESOLUTION.height} (4K) for best quality`,
    );
  }

  // Duration warning (above recommended but below max)
  if (constraints?.recommendedMax) {
    if (metadata.duration > constraints.recommendedMax && metadata.duration <= constraints.max) {
      warnings.push(
        `Duration ${formatDuration(metadata.duration)} exceeds recommended ` +
          `${formatDuration(constraints.recommendedMax)} for ${assetKind.toLowerCase()}`,
      );
    }
  }

  // Aspect ratio warning
  if (!isExpectedAspectRatio(metadata.aspectRatio)) {
    warnings.push(
      `Aspect ratio ${metadata.aspectRatio} is non-standard. ` +
        `Expected ratios: ${EXPECTED_ASPECT_RATIOS.join(", ")}`,
    );
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

// === Hook Interface ===

interface UseVideoUploadOptions {
  assetKind: "FEATURE" | "TRAILER";
  submissionId?: string | null;
  titleId?: string | null;
  mode: "submission" | "title";
  maxSizeGB: number;
  assetId?: string; // For replacements
}

interface UseVideoUploadReturn {
  // File state
  selectedFile: File | null;
  setSelectedFile: (file: File | null) => void;

  // Metadata and validation state
  metadataState: VideoMetadataState;

  // Upload state (managed externally for now)
  uploading: boolean;
  setUploading: (uploading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;

  // Actions
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  clearSelection: () => void;

  // Helpers
  canUpload: boolean; // True if file selected and validation passed (or extraction failed)
  getClientMetadataForUpload: () => Record<string, unknown> | undefined;
}

/**
 * Hook for video upload with client-side metadata extraction and validation
 *
 * This hook extracts video metadata using the HTML5 video element and validates
 * against quality requirements. Validation errors block upload, while warnings
 * allow upload to proceed.
 *
 * If metadata extraction fails (unsupported format, corrupt file, etc.), the upload
 * is still allowed to proceed - backend validation will catch any issues.
 */
export function useVideoUpload({ assetKind, maxSizeGB }: UseVideoUploadOptions): UseVideoUploadReturn {
  // File state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Metadata state
  const [metadataState, setMetadataState] = useState<VideoMetadataState>({
    metadata: null,
    validation: null,
    extractionFailed: false,
  });

  // Upload state (exposed for VideoUploadZone to manage)
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Handle file selection with metadata extraction and validation
   */
  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Reset state
      setError(null);
      setMetadataState({ metadata: null, validation: null, extractionFailed: false });

      // Basic file type check
      if (!file.type.startsWith("video/")) {
        setError("Please select a video file");
        return;
      }

      // File size check (quick, before metadata extraction)
      const maxBytes = maxSizeGB * 1024 * 1024 * 1024;
      if (file.size > maxBytes) {
        setError(`Video must be smaller than ${maxSizeGB}GB`);
        return;
      }

      setSelectedFile(file);

      // Extract metadata (non-blocking - continues even if this fails)
      try {
        const metadata = await extractVideoMetadata(file);

        // Validate metadata
        const validation = validateVideoMetadata(metadata, {
          assetKind,
          fileSize: file.size,
          maxSizeGB,
        });

        setMetadataState({
          metadata,
          validation,
          extractionFailed: false,
        });

        // Set error if validation failed (blocking)
        if (!validation.valid) {
          setError(validation.errors[0]);
        }
      } catch (extractionError) {
        // Metadata extraction failed - allow upload anyway
        console.warn("Video metadata extraction failed:", extractionError);
        setMetadataState({
          metadata: null,
          validation: null,
          extractionFailed: true,
        });
        // No error set - user can still upload, backend will validate
      }
    },
    [assetKind, maxSizeGB],
  );

  /**
   * Clear file selection and reset state
   */
  const clearSelection = useCallback(() => {
    setSelectedFile(null);
    setMetadataState({ metadata: null, validation: null, extractionFailed: false });
    setError(null);
  }, []);

  /**
   * Check if upload can proceed
   * - File must be selected
   * - Either validation passed OR extraction failed (backend will validate)
   */
  const canUpload =
    selectedFile !== null &&
    (metadataState.extractionFailed || metadataState.validation === null || metadataState.validation.valid);

  /**
   * Get client metadata for upload request body
   * Returns undefined if no metadata extracted (backend will skip client metadata)
   */
  const getClientMetadataForUpload = useCallback((): Record<string, unknown> | undefined => {
    if (!metadataState.metadata) return undefined;

    return {
      duration: metadataState.metadata.duration,
      width: metadataState.metadata.width,
      height: metadataState.metadata.height,
      aspectRatio: metadataState.metadata.aspectRatio,
      validationSource: "client",
    };
  }, [metadataState.metadata]);

  return {
    selectedFile,
    setSelectedFile,
    metadataState,
    uploading,
    setUploading,
    error,
    setError,
    handleFileSelect,
    clearSelection,
    canUpload,
    getClientMetadataForUpload,
  };
}

// Re-export for convenience
export { formatDuration, formatFileSize };
